import { z } from "zod";
import {
  PaginationRequestSchema,
  PaginationResponseSchema,
} from "./pagination";
import { TournamentFormatConfigSchema } from "./tournament";

// ======================
// QUERY SCHEMAS
// ======================
export const FixtureStatusQuerySchema = PaginationRequestSchema.extend({
  name: z.string().optional(),
});
export type FixtureStatusQueryDTO = z.infer<typeof FixtureStatusQuerySchema>;

export const ParticipantStatusQuerySchema = PaginationRequestSchema.extend({
  name: z.string().optional(),
});
export type ParticipantStatusQueryDTO = z.infer<
  typeof ParticipantStatusQuerySchema
>;

export const TournamentFormatQuerySchema = PaginationRequestSchema.extend({
  name: z.string().optional(),
  sport: z.string().optional(),
  createdFrom: z.string().optional(),
  createdTo: z.string().optional(),
  subCategoryId: z.string().optional(),
  search: z.string().optional(),
});
export type TournamentFormatQueryDTO = z.infer<
  typeof TournamentFormatQuerySchema
>;

export const TournamentStageQuerySchema = PaginationRequestSchema.extend({
  seasonId: z.string().optional(),
  stageType: z.string().optional(),
});
export type TournamentStageQueryDTO = z.infer<
  typeof TournamentStageQuerySchema
>;

export const TournamentGroupQuerySchema = PaginationRequestSchema.extend({
  stageId: z.string().optional(),
});
export type TournamentGroupQueryDTO = z.infer<
  typeof TournamentGroupQuerySchema
>;

export const TournamentKnockoutSlotQuerySchema = PaginationRequestSchema.extend(
  {
    stageId: z.string().optional(),
  },
);
export type TournamentKnockoutSlotQueryDTO = z.infer<
  typeof TournamentKnockoutSlotQuerySchema
>;

export const TournamentStandingQuerySchema = PaginationRequestSchema.extend({
  seasonId: z.string().optional(),
  groupId: z.string().optional(),
});
export type TournamentStandingQueryDTO = z.infer<
  typeof TournamentStandingQuerySchema
>;

export const FixturePeriodQuerySchema = PaginationRequestSchema.extend({
  fixtureId: z.string().optional(),
});
export type FixturePeriodQueryDTO = z.infer<typeof FixturePeriodQuerySchema>;

export const TournamentStatusQuerySchema = PaginationRequestSchema.extend({
  name: z.string().optional(),
});
export type TournamentStatusQueryDTO = z.infer<
  typeof TournamentStatusQuerySchema
>;

// ======================
// FIXTURE STATUS
// ======================
export const FixtureStatusResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  isActive: z.boolean(),
  isFinal: z.boolean(),
});
export type FixtureStatusResponseDTO = z.infer<
  typeof FixtureStatusResponseSchema
>;

export const FixtureStatusesPaginatedResponseSchema = PaginationResponseSchema(
  FixtureStatusResponseSchema,
);
export type FixtureStatusesPaginatedResponseDTO = z.infer<
  typeof FixtureStatusesPaginatedResponseSchema
>;

export const CreateFixtureStatusRequestSchema = z.object({
  name: z.string().min(1, "El nombre es requerido"),
  isActive: z.boolean().optional().default(true),
  isFinal: z.boolean().optional().default(false),
});
export type CreateFixtureStatusRequestDTO = z.infer<
  typeof CreateFixtureStatusRequestSchema
>;

export const UpdateFixtureStatusRequestSchema = z.object({
  name: z.string().min(1, "El nombre es requerido").optional(),
  isActive: z.boolean().optional(),
  isFinal: z.boolean().optional(),
});
export type UpdateFixtureStatusRequestDTO = z.infer<
  typeof UpdateFixtureStatusRequestSchema
>;

// ======================
// PARTICIPANT STATUS
// ======================
export const ParticipantStatusResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  canPlay: z.boolean(),
  isRanked: z.boolean(),
});
export type ParticipantStatusResponseDTO = z.infer<
  typeof ParticipantStatusResponseSchema
>;

export const ParticipantStatusesPaginatedResponseSchema =
  PaginationResponseSchema(ParticipantStatusResponseSchema);
export type ParticipantStatusesPaginatedResponseDTO = z.infer<
  typeof ParticipantStatusesPaginatedResponseSchema
>;

export const CreateParticipantStatusRequestSchema = z.object({
  name: z.string().min(1, "El nombre es requerido"),
  canPlay: z.boolean().optional().default(true),
  isRanked: z.boolean().optional().default(true),
});
export type CreateParticipantStatusRequestDTO = z.infer<
  typeof CreateParticipantStatusRequestSchema
>;

export const UpdateParticipantStatusRequestSchema = z.object({
  name: z.string().min(1, "El nombre es requerido").optional(),
  canPlay: z.boolean().optional(),
  isRanked: z.boolean().optional(),
});
export type UpdateParticipantStatusRequestDTO = z.infer<
  typeof UpdateParticipantStatusRequestSchema
>;

// ======================
// TOURNAMENT FORMAT
// ======================
export const TournamentFormatResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  sport: z.string(),
  config: TournamentFormatConfigSchema.nullable(),
  createdAt: z.coerce.date(),
});
export type TournamentFormatResponseDTO = z.infer<
  typeof TournamentFormatResponseSchema
>;

export const TournamentFormatsPaginatedResponseSchema =
  PaginationResponseSchema(TournamentFormatResponseSchema);
export type TournamentFormatsPaginatedResponseDTO = z.infer<
  typeof TournamentFormatsPaginatedResponseSchema
>;

export const CreateTournamentFormatRequestSchema = z.object({
  name: z.string().min(1, "El nombre es requerido"),
  sport: z.string().min(1, "El deporte es requerido"),
  config: TournamentFormatConfigSchema.optional(),
});
export type CreateTournamentFormatRequestDTO = z.infer<
  typeof CreateTournamentFormatRequestSchema
>;

export const UpdateTournamentFormatRequestSchema = z.object({
  name: z.string().min(1, "El nombre es requerido").optional(),
  sport: z.string().min(1, "El deporte es requerido").optional(),
  config: TournamentFormatConfigSchema.optional(),
});
export type UpdateTournamentFormatRequestDTO = z.infer<
  typeof UpdateTournamentFormatRequestSchema
>;

// ======================
// TOURNAMENT STAGE (read-only)
// ======================
export const TournamentStageResponseSchema = z.object({
  id: z.string(),
  seasonId: z.string(),
  name: z.string(),
  stageType: z.enum(["group", "knockout"]),
  stageOrder: z.number(),
});
export type TournamentStageResponseDTO = z.infer<
  typeof TournamentStageResponseSchema
>;

export const TournamentStagesPaginatedResponseSchema = PaginationResponseSchema(
  TournamentStageResponseSchema,
);
export type TournamentStagesPaginatedResponseDTO = z.infer<
  typeof TournamentStagesPaginatedResponseSchema
>;

// ======================
// TOURNAMENT GROUP
// ======================
export const TournamentGroupResponseSchema = z.object({
  id: z.string(),
  stageId: z.string(),
  name: z.string(),
});
export type TournamentGroupResponseDTO = z.infer<
  typeof TournamentGroupResponseSchema
>;

export const TournamentGroupsPaginatedResponseSchema = PaginationResponseSchema(
  TournamentGroupResponseSchema,
);
export type TournamentGroupsPaginatedResponseDTO = z.infer<
  typeof TournamentGroupsPaginatedResponseSchema
>;

export const CreateTournamentGroupRequestSchema = z.object({
  stageId: z.string().min(1, "El ID de etapa es requerido"),
  name: z.string().min(1, "El nombre es requerido"),
});
export type CreateTournamentGroupRequestDTO = z.infer<
  typeof CreateTournamentGroupRequestSchema
>;

export const UpdateTournamentGroupRequestSchema = z.object({
  stageId: z.string().optional(),
  name: z.string().min(1, "El nombre es requerido").optional(),
});
export type UpdateTournamentGroupRequestDTO = z.infer<
  typeof UpdateTournamentGroupRequestSchema
>;

// ======================
// TOURNAMENT KNOCKOUT SLOT (read-only)
// ======================
export const TournamentKnockoutSlotResponseSchema = z.object({
  id: z.string(),
  stageId: z.string(),
  slotKey: z.string(),
  parentSlotId: z.string().nullable(),
});
export type TournamentKnockoutSlotResponseDTO = z.infer<
  typeof TournamentKnockoutSlotResponseSchema
>;

export const TournamentKnockoutSlotsPaginatedResponseSchema =
  PaginationResponseSchema(TournamentKnockoutSlotResponseSchema);
export type TournamentKnockoutSlotsPaginatedResponseDTO = z.infer<
  typeof TournamentKnockoutSlotsPaginatedResponseSchema
>;

// ======================
// TOURNAMENT STANDING (read-only)
// ======================
export const FormEntrySchema = z.object({
  matchId: z.string(),
  result: z.enum(["W", "L", "D"]),
  score: z.string(),
  opponentId: z.string(),
  isHome: z.boolean(),
});

export const TournamentStandingResponseSchema = z.object({
  seasonId: z.string(),
  groupId: z.string().nullable(),
  participantId: z.string(),
  participantName: z.string().nullable(),
  participantLogo: z.string().nullable(),
  teamId: z.string().nullable(),
  played: z.number(),
  won: z.number(),
  drawn: z.number(),
  lost: z.number(),
  scoreFor: z.number(),
  scoreAgainst: z.number(),
  scoreDiff: z.number(),
  points: z.number(),
  winPercentage: z.number(),
  rank: z.number().nullable(),
  fairPlayScore: z.number().nullable(),
  formHistory: z.array(FormEntrySchema),
  group: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable(),
  country: z
    .object({
      id: z.string(),
      name: z.string(),
      flag: z.string().nullable(),
    })
    .nullable(),
});
export type TournamentStandingResponseDTO = z.infer<
  typeof TournamentStandingResponseSchema
>;

export const TournamentStandingsPaginatedResponseSchema =
  PaginationResponseSchema(TournamentStandingResponseSchema);
export type TournamentStandingsPaginatedResponseDTO = z.infer<
  typeof TournamentStandingsPaginatedResponseSchema
>;

export const AssignTournamentStandingGroupRequestSchema = z.object({
  seasonId: z.string().min(1, "El ID de temporada es requerido"),
  participantId: z.string().min(1, "El participante es requerido"),
  groupId: z.string().nullable(),
});
export type AssignTournamentStandingGroupRequestDTO = z.infer<
  typeof AssignTournamentStandingGroupRequestSchema
>;

export const SyncTournamentStandingGroupsRequestSchema = z.object({
  seasonId: z.string().min(1, "El ID de temporada es requerido"),
  stageOrder: z.number().int().min(1).optional(),
  stageName: z.string().min(1).optional(),
  teamsPerGroup: z.number().int().min(1),
  groups: z.array(
    z.object({
      id: z.string(),
      name: z.string().min(1),
    }),
  ),
  assignments: z.record(z.string(), z.string().nullable()),
});
export type SyncTournamentStandingGroupsRequestDTO = z.infer<
  typeof SyncTournamentStandingGroupsRequestSchema
>;

export const TournamentStandingsSeasonQuerySchema = z.object({
  grouped: z.boolean().optional(),
});
export type TournamentStandingsSeasonQueryDTO = z.infer<
  typeof TournamentStandingsSeasonQuerySchema
>;

// === grouped standings response ===
export const TournamentStandingGroupedSchema = z.object({
  group: TournamentStandingResponseSchema.shape.group,
  standings: z.array(TournamentStandingResponseSchema.omit({ group: true })),
});
export type TournamentStandingGroupedDTO = z.infer<
  typeof TournamentStandingGroupedSchema
>;
